/**
 * typingPresence — who is typing in a shared room, and for how long that is true.
 *
 * T-R15 (Miles, 2026-09-21): *"there should also be a functionality to see when
 * someone is typing it should show a list like this in there consistently when
 * human users are typing as well. when there's no changes to the draft box in 4
 * seconds it should not say [user name] is responding."*
 *
 * PURE — no React, no Firebase, no clock of its own (every function takes `nowMs`).
 * The repository writes and listens; the host renders; this file decides what the
 * rows MEAN. Three properties are load-bearing and are pinned:
 *
 *  1. NEVER THE DRAFT. A typing doc carries a uid, a server stamp and a schema
 *     version — not the text, not its length, not a word count. There is nothing
 *     here to leak because nothing about what a person wrote is ever published.
 *  2. NEVER THE VIEWER. Your own row is dropped on the way out, so the room shows
 *     you other people and never yourself typing at yourself.
 *  3. NEVER A ROLE (R8). A row is a NAME and a glyph. Standing, roles and access
 *     are not facts of a keystroke, and this file has no field that could carry one.
 *
 * The EXPIRY is deliberately doubled up, and both halves are needed:
 *   - the WRITER deletes its own doc after TYPING_IDLE_CLEAR_MS of no change (the
 *     4 seconds Miles named), on send, and on leaving the thread; that is the
 *     normal path and it is what makes the row disappear promptly.
 *   - the READER additionally drops any row whose stamp is older than
 *     TYPING_STALE_MS, because the writer is a browser tab and a tab can die
 *     mid-draft — a crashed laptop must not leave "Miles is typing…" on a
 *     colleague's screen forever. The stamp is the SERVER's (`request.time`,
 *     enforced in firestore.rules), so a client cannot hold a row open by lying
 *     about when it typed.
 */

/** The collection under a thread's shared-store document. */
export const TYPING_COLLECTION = 'typing';

/** Shape version on every doc; a row that does not match is ignored, never guessed at. */
export const TYPING_SCHEMA_VERSION = 1;

/**
 * No change to the draft for this long ⇒ the writer clears its row. Miles named
 * the number: "when there's no changes to the draft box in 4 seconds it should
 * not say [user name] is responding".
 */
export const TYPING_IDLE_CLEAR_MS = 4_000;

/**
 * At most one write per typist per this interval while they keep typing. A
 * keystroke is not a write: a person typing steadily produces one ping every
 * 1.5 s, and the row stays alive because each ping re-stamps it.
 */
export const TYPING_PUBLISH_MIN_MS = 1_500;

/**
 * The READER's belt for a writer that never got to clean up (a closed laptop, a
 * killed tab, a lost network). Longer than the idle clear so it never races the
 * normal path — a row the writer is about to delete is not yanked out from under
 * it — and short enough that a dead tab's row is gone within seconds.
 */
export const TYPING_STALE_MS = 6_000;

/** Rows drawn before the list collapses to "+N more". */
export const TYPING_ROWS_MAX = 3;

/** One person, currently typing, as the store holds them. */
export interface TypingView {
  uid: string;
  /** Server stamp (`request.time`), in ms. */
  updatedAtMs: number;
}

/** One person, currently typing, as the room draws them. */
export interface TypingRow {
  uid: string;
  updatedAtMs: number;
}

export interface TypingRowsView {
  /** Ordered oldest-first (whoever started typing first reads first), capped. */
  rows: TypingRow[];
  /** How many typists the cap left out. */
  hidden: number;
}

/**
 * A number, or nothing. Deliberately NOT `Number(v)`: `Number(null)` is 0, so a
 * coercing reader turns an UNSTAMPED row — the instant between a local write and
 * the server's acknowledgement — into one written at the epoch, which then reads
 * as stale rather than as unusable. Same answer in the end, arrived at by an
 * accident; a row with no stamp has no instant, and says so.
 */
function finiteMs(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;
}

/**
 * Tolerant parse of one stored doc. Returns null rather than throwing for every
 * malformed shape — an older or hostile row is ignored, and the room simply does
 * not show that person typing.
 *
 * `updatedAt` may arrive as a Firestore Timestamp (`toMillis`), a number, or —
 * for the instant between a local write and the server's acknowledgement — null,
 * which is treated as "no usable stamp" and dropped: an unstamped row would
 * otherwise be indistinguishable from an infinitely fresh one.
 */
export function fromTypingDoc(id: string, raw: unknown): TypingView | null {
  const uid = (id ?? '').trim();
  if (!uid) return null;
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (finiteMs(r.schemaVersion) !== TYPING_SCHEMA_VERSION) return null;
  // The doc id is the authority on WHO — a `uid` field that disagrees with the
  // path is a row we refuse rather than reconcile (the rules pin them equal, so
  // a disagreement means something we do not understand wrote it).
  if (typeof r.uid === 'string' && r.uid.trim() && r.uid.trim() !== uid) return null;
  const stamp = r.updatedAt as { toMillis?: () => number } | number | null | undefined;
  const updatedAtMs = typeof stamp === 'object' && stamp && typeof stamp.toMillis === 'function'
    ? finiteMs(stamp.toMillis())
    : finiteMs(stamp);
  if (updatedAtMs === null) return null;
  return { uid, updatedAtMs };
}

export interface ComposeTypingRowsInput {
  docs: ReadonlyArray<TypingView | null | undefined>;
  /** The person looking — their own row is never drawn. */
  viewerUid: string;
  nowMs: number;
  /** Only these people are drawn, when the room's roster is known. */
  participantUids?: ReadonlyArray<string> | null;
  max?: number;
  staleMs?: number;
}

/**
 * The rows the room draws. Pure, and deliberately strict about who survives:
 * the viewer is dropped, a stale stamp is dropped, and — when the thread's
 * participants are known — anyone outside the room is dropped, because a
 * presence row for someone who is not here is a fact about a room this is not.
 * An UNKNOWN roster (undefined/null) draws everyone the store returned: the
 * rules already restrict the collection to participants, so failing closed on a
 * roster the client has not loaded yet would blank the list for a moment on
 * every thread open for no safety gain.
 */
export function composeTypingRows(input: ComposeTypingRowsInput): TypingRowsView {
  const max = Math.max(1, Math.floor(input.max ?? TYPING_ROWS_MAX));
  const staleMs = input.staleMs ?? TYPING_STALE_MS;
  const viewer = (input.viewerUid || '').trim();
  const roster = input.participantUids && input.participantUids.length
    ? new Set(input.participantUids.map((u) => (u || '').trim()).filter(Boolean))
    : null;

  const seen = new Set<string>();
  const live: TypingRow[] = [];
  for (const d of input.docs ?? []) {
    if (!d || !d.uid) continue;
    if (viewer && d.uid === viewer) continue;
    if (roster && !roster.has(d.uid)) continue;
    if (input.nowMs - d.updatedAtMs > staleMs) continue;
    if (seen.has(d.uid)) continue;
    seen.add(d.uid);
    live.push({ uid: d.uid, updatedAtMs: d.updatedAtMs });
  }
  // Oldest stamp first: whoever has been typing longest reads at the top, and
  // the order does not reshuffle under the reader every time someone presses a
  // key (which sorting by NEWEST would do).
  live.sort((a, b) => (a.updatedAtMs - b.updatedAtMs) || (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0));
  return { rows: live.slice(0, max), hidden: Math.max(0, live.length - max) };
}

/**
 * Should the writer publish a ping right now? Pure so the host effect holds no
 * policy: it publishes when the draft changed and the last ping is older than
 * the interval, which turns a burst of keystrokes into one write.
 */
export function shouldPublishTyping(lastPublishedAtMs: number | null, nowMs: number): boolean {
  if (lastPublishedAtMs === null) return true;
  return nowMs - lastPublishedAtMs >= TYPING_PUBLISH_MIN_MS;
}
